import PropTypes from 'prop-types'
import { MapPin, Building2, Trophy } from 'lucide-react'
import { useState } from 'react'
import AiDataSummary from './AiDataSummary'

/**
 * AiTableResult Component
 *
 * Displays tabular data from query responses:
 * - Rankings (comparison type)
 * - Facilities (filter type)
 * - Data object (answer type)
 */
export default function AiTableResult({
  data,
  rankings,
  facilities,
  resultCount,
}) {
  const [showAllFacilities, setShowAllFacilities] = useState(false)

  const hasRankings = rankings && rankings.length > 0
  const hasFacilities = facilities && facilities.length > 0
  const hasData = data && Object.keys(data).length > 0

  const displayedFacilities = showAllFacilities
    ? facilities
    : facilities?.slice(0, 10) || []

  return (
    <div className="p-6 space-y-6">
      {/* Rankings Table */}
      {hasRankings && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-purple-600" />
            <h3 className="font-semibold text-gray-900">Rankings</h3>
          </div>
          <div className="space-y-2">
            {rankings.map((item, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-100 hover:bg-purple-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-8 h-8 bg-purple-600 text-white rounded-full font-bold text-sm">
                    #{index + 1}
                  </div>
                  <span className="font-medium text-gray-900">
                    {item.location}
                  </span>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-purple-600">
                    {item.value}
                  </div>
                  <div className="text-xs text-gray-600">{item.metric}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Facilities Table */}
      {hasFacilities && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-green-600" />
              <h3 className="font-semibold text-gray-900">
                Matching Facilities{' '}
                <span className="text-sm font-normal text-gray-600">
                  ({resultCount || facilities.length} total)
                </span>
              </h3>
            </div>
          </div>

          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {displayedFacilities.map((facility, idx) => (
              <div
                key={idx}
                className="p-4 bg-green-50 rounded-lg border border-green-100 hover:bg-green-100 transition-colors"
              >
                <div className="font-medium text-gray-900 mb-2">
                  {facility.institution_name ||
                    facility.facility_name ||
                    facility.name ||
                    'Unnamed Facility'}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-700">
                  {/* Location */}
                  {(facility.district ||
                    facility.subcounty ||
                    facility.parish) && (
                    <div className="flex items-center gap-1.5">
                      <MapPin className="w-4 h-4 text-gray-500" />
                      <span>
                        {[
                          facility.parish,
                          facility.subcounty,
                          facility.district,
                        ]
                          .filter(Boolean)
                          .join(', ')}
                      </span>
                    </div>
                  )}

                  {/* Ownership/Level */}
                  {(facility.ownership || facility.facility_level) && (
                    <div className="flex items-center gap-1.5">
                      <Building2 className="w-4 h-4 text-gray-500" />
                      <span>
                        {facility.ownership || facility.facility_level}
                      </span>
                    </div>
                  )}

                  {/* Education-specific */}
                  {facility.total_learners && (
                    <div>
                      👥 {facility.total_learners} learners •{' '}
                      {facility.total_teachers} teachers
                    </div>
                  )}
                  {facility.pupil_teacher_ratio && (
                    <div>📊 PTR: {facility.pupil_teacher_ratio}</div>
                  )}

                  {/* Health-specific */}
                  {facility.hasImmunization && (
                    <div>
                      💉 Immunization:{' '}
                      {facility.hasImmunization === 'Yes' ? '✓' : '✗'}
                    </div>
                  )}
                  {facility.hasLaboratory && (
                    <div>
                      🔬 Laboratory:{' '}
                      {facility.hasLaboratory === 'Yes' ? '✓' : '✗'}
                    </div>
                  )}

                  {/* Common facilities */}
                  {facility.electricity_available && (
                    <div>
                      ⚡ Electricity:{' '}
                      {facility.electricity_available === 'Yes' ? '✓' : '✗'}
                    </div>
                  )}
                  {facility.internet_access && (
                    <div>
                      🌐 Internet:{' '}
                      {facility.internet_access === 'Yes' ? '✓' : '✗'}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Show More/Less Button */}
          {facilities.length > 10 && (
            <div className="text-center pt-2">
              <button
                onClick={() => setShowAllFacilities(!showAllFacilities)}
                className="px-4 py-2 text-sm font-medium text-green-700 hover:text-green-900 bg-green-50 hover:bg-green-100 rounded-lg transition-colors border border-green-200"
              >
                {showAllFacilities
                  ? 'Show Less'
                  : `Show ${facilities.length - 10} More Facilities`}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Data Object Display */}
      {hasData && (
        <div className="space-y-3">
          {/* Check if data is normalized (data_summary type) */}
          {data.type === 'data_summary' ? (
            <AiDataSummary data={data} />
          ) : (
            <>
              {/* Legacy format - original key-value display */}
              <h3 className="font-semibold text-gray-900">Data Summary</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {Object.entries(data).map(([key, value]) => {
                  // Format value based on type
                  let formattedValue
                  if (typeof value === 'number') {
                    formattedValue = value.toLocaleString()
                  } else if (typeof value === 'object' && value !== null) {
                    // Format objects and arrays as JSON
                    formattedValue = (
                      <pre className="text-xs whitespace-pre-wrap break-words font-mono mt-1">
                        {JSON.stringify(value, null, 2)}
                      </pre>
                    )
                  } else if (typeof value === 'boolean') {
                    formattedValue = value ? '✓ Yes' : '✗ No'
                  } else {
                    formattedValue = String(value)
                  }

                  return (
                    <div
                      key={key}
                      className="p-3 bg-blue-50 rounded-lg border border-blue-100"
                    >
                      <div className="text-xs font-medium text-blue-700 uppercase mb-1">
                        {key.replace(/([A-Z])/g, ' $1').trim()}
                      </div>
                      <div className="text-lg font-bold text-blue-900">
                        {formattedValue}
                      </div>
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

AiTableResult.propTypes = {
  type: PropTypes.string,
  data: PropTypes.object,
  rankings: PropTypes.arrayOf(
    PropTypes.shape({
      location: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
        .isRequired,
      metric: PropTypes.string.isRequired,
    })
  ),
  facilities: PropTypes.arrayOf(PropTypes.object),
  resultCount: PropTypes.number,
}
